import React from 'react';
import { topographicContours, elevationLabels } from './topography.generated';

/**
 * TopographicMapFull - Mapa topográfico realista completo
 * Curvas topográficas generadas algorítmicamente desde campo escalar
 * Infraestructura, ruta táctica y marcadores overlay sobre terreno
 */
const TopographicMapFull = () => {
  return (
    <svg viewBox="0 0 2200 1300" preserveAspectRatio="xMidYMid slice" role="presentation">
      <defs>
        <pattern id="gridMinor" width="72" height="72" patternUnits="userSpaceOnUse">
          <path d="M72 0H0V72" fill="none" stroke="rgba(198,210,202,.045)" strokeWidth="1" />
        </pattern>
        <pattern id="gridMajor" width="360" height="360" patternUnits="userSpaceOnUse">
          <rect width="360" height="360" fill="url(#gridMinor)" />
          <path d="M360 0H0V360" fill="none" stroke="rgba(206,217,209,.075)" strokeWidth="1.25" />
        </pattern>
        <pattern id="forestDots" width="28" height="28" patternUnits="userSpaceOnUse">
          <circle cx="5" cy="8" r="1.2" fill="rgba(136,156,126,.16)" />
          <circle cx="19" cy="18" r="1" fill="rgba(136,156,126,.12)" />
          <path d="M10 24l4-7 4 7z" fill="none" stroke="rgba(136,156,126,.12)" strokeWidth="1" />
        </pattern>
        <filter id="blueGlow" x="-70%" y="-70%" width="240%" height="240%">
          <feGaussianBlur stdDeviation="3.5" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <linearGradient id="terrainFade" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#0b1110" />
          <stop offset=".55" stopColor="#0c1312" />
          <stop offset="1" stopColor="#0a100f" />
        </linearGradient>
        <marker id="routeArrow" viewBox="0 0 10 10" refX="8.2" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M0 0L10 5L0 10z" fill="#56a8ff" />
        </marker>
      </defs>

      <rect width="2200" height="1300" fill="url(#terrainFade)" />
      <rect width="2200" height="1300" fill="url(#gridMajor)" />

      {/* Faint terrain regions */}
      <path d="M1180 1060C1260 952 1348 918 1445 900C1534 883 1608 827 1668 731C1734 625 1829 579 1970 600L2200 645V1300H1120Z"
            fill="rgba(98,126,100,.055)" />
      <path d="M1380 180C1500 128 1676 134 1808 222C1905 287 2000 301 2200 258V0H1438Z"
            fill="rgba(101,124,98,.04)" />
      <path d="M1250 690C1390 608 1506 586 1628 612C1740 636 1822 612 1918 540L2010 770C1876 850 1734 870 1592 844C1450 820 1340 842 1230 928Z"
            fill="url(#forestDots)" opacity=".9" />

      {/* Generated topographic contours */}
      <g className="topographic-contours" fill="none" strokeLinecap="round" strokeLinejoin="round">
        {topographicContours.map((contour, i) => (
          <path
            key={i}
            d={contour.path}
            stroke={contour.isMajor ? 'rgba(198,204,195,.24)' : 'rgba(176,188,181,.13)'}
            strokeWidth={contour.isMajor ? '1.15' : '0.85'}
            vectorEffect="non-scaling-stroke"
          />
        ))}
      </g>

      {/* Elevation labels */}
      <g fill="rgba(177,193,184,.22)" fontFamily="ui-monospace, monospace" fontSize="13" letterSpacing="1.5">
        {elevationLabels.map((label, i) => (
          <text key={i} x={label.x} y={label.y}>{label.elevation}</text>
        ))}
      </g>

      {/* Stream follows valley */}
      <path d="M786 1130C906 1033 966 980 1032 891C1099 800 1124 712 1111 629C1098 546 1127 478 1196 422C1268 364 1321 299 1365 207"
            fill="none" stroke="rgba(83,160,177,.50)" strokeWidth="3" strokeLinecap="round" />
      <path d="M795 1138C915 1041 977 987 1042 899C1110 807 1138 713 1124 627C1110 538 1137 472 1208 415C1280 356 1335 291 1378 199"
            fill="none" stroke="rgba(83,160,177,.16)" strokeWidth="8" strokeLinecap="round" />

      {/* Road and settlement */}
      <path d="M990 1168C1056 1079 1128 1018 1210 956C1319 873 1392 784 1434 692C1471 611 1529 549 1601 502C1667 459 1712 406 1745 331"
            fill="none" stroke="rgba(202,196,174,.15)" strokeWidth="13" strokeLinecap="round" />
      <path d="M990 1168C1056 1079 1128 1018 1210 956C1319 873 1392 784 1434 692C1471 611 1529 549 1601 502C1667 459 1712 406 1745 331"
            fill="none" stroke="rgba(216,211,194,.32)" strokeWidth="2.2" strokeLinecap="round" />
      <path d="M991 1168C1056 1079 1128 1018 1210 956C1319 873 1392 784 1434 692C1471 611 1529 549 1601 502C1667 459 1712 406 1745 331"
            fill="none" stroke="rgba(9,13,12,.9)" strokeWidth="1" strokeDasharray="12 9" />

      {/* Bridge */}
      <g transform="translate(1120 1000) rotate(-42)">
        <path d="M-18 -8H18M-18 8H18" stroke="rgba(220,216,199,.34)" strokeWidth="3" />
        <path d="M-14 -12V12M14 -12V12" stroke="rgba(220,216,199,.24)" strokeWidth="1.5" />
      </g>

      {/* Compact compound at objective */}
      <g fill="rgba(215,217,207,.10)" stroke="rgba(215,217,207,.25)" strokeWidth="1.4">
        <rect x="1730" y="280" width="42" height="28" transform="rotate(-8 1751 294)" />
        <rect x="1782" y="307" width="35" height="25" transform="rotate(11 1799 319)" />
        <rect x="1705" y="330" width="31" height="22" transform="rotate(-3 1720 341)" />
        <rect x="1816" y="268" width="27" height="21" transform="rotate(7 1829 278)" />
      </g>

      {/* Map labels */}
      <g fill="rgba(177,193,184,.36)" fontFamily="ui-monospace, monospace" fontSize="16" letterSpacing="2">
        <text x="365" y="760">RIDGE 214</text>
        <text x="980" y="846" transform="rotate(-56 980 846)">Kestrel Creek</text>
        <text x="1180" y="1030">BRIDGE 07</text>
        <text x="1615" y="565">NORTH ACCESS</text>
        <text x="1728" y="246">HILL 182</text>
        <text x="1416" y="766" transform="rotate(-52 1416 766)">ROUTE BASTION</text>
      </g>

      {/* Grid coordinates */}
      <g fill="rgba(164,180,171,.24)" fontFamily="ui-monospace, monospace" fontSize="14" letterSpacing="2">
        <text x="1450" y="95">37T EK 042 318</text>
        <text x="2050" y="610">E6</text>
        <text x="1510" y="1195">E7</text>
      </g>

      {/* Tactical restricted area */}
      <path d="M1328 812L1482 724L1588 797L1498 904L1365 914Z"
            fill="rgba(176,188,181,.025)" stroke="rgba(176,188,181,.17)" strokeWidth="1.4" strokeDasharray="11 10" />
      <text x="1418" y="826" fill="rgba(176,188,181,.30)" fontFamily="ui-monospace, monospace" fontSize="14" letterSpacing="2">NO-FIRE AREA</text>

      {/* NATO-style operational layer */}
      <path className="route-flow" d="M1086 1094C1156 1019 1225 957 1305 894C1390 828 1440 759 1474 690C1510 617 1561 564 1628 518C1689 476 1729 425 1760 353"
            fill="none" stroke="rgba(86,168,255,.72)" strokeWidth="3.2" strokeLinecap="round" markerEnd="url(#routeArrow)" filter="url(#blueGlow)" />

      {/* Start / friendly unit */}
      <g transform="translate(1078 1098)" fill="none" stroke="#56a8ff" strokeWidth="2.4" filter="url(#blueGlow)">
        <rect x="-38" y="-25" width="76" height="50" rx="1" />
        <path d="M-38 -6H38M-12 -25V25M12 -25V25" opacity=".62" />
      </g>
      <g fill="#7fbeff" fontFamily="ui-monospace, monospace" letterSpacing="2">
        <text x="1038" y="1167" fontSize="17">ALPHA 1-1</text>
        <text x="1041" y="1189" fontSize="12" opacity=".66">LD · 21:42Z</text>
      </g>

      {/* Waypoint 01: bridge crossing */}
      <g transform="translate(1218 948)" filter="url(#blueGlow)">
        <circle r="8" fill="#56a8ff" />
        <circle r="17" fill="none" stroke="rgba(86,168,255,.35)" strokeWidth="1.4" />
      </g>
      <text x="1242" y="954" fill="#7fbeff" fontFamily="ui-monospace, monospace" fontSize="16" letterSpacing="2">WP-01 · BRIDGE</text>

      {/* Waypoint 02: saddle before objective */}
      <g transform="translate(1510 626)" filter="url(#blueGlow)">
        <circle r="8" fill="#56a8ff" />
        <circle r="17" fill="none" stroke="rgba(86,168,255,.35)" strokeWidth="1.4" />
      </g>
      <text x="1534" y="632" fill="#7fbeff" fontFamily="ui-monospace, monospace" fontSize="16" letterSpacing="2">WP-02 · SADDLE</text>

      {/* Objective anchored to compound on hill */}
      <g className="objective-pulse" transform="translate(1776 314)" fill="none" stroke="#56a8ff" filter="url(#blueGlow)">
        <circle r="58" strokeWidth="2.4" />
        <circle r="42" strokeWidth="1.3" opacity=".72" />
        <path d="M-69 0H69M0-69V69" strokeWidth="1.2" opacity=".8" />
        <path d="M-12 -12L12 12M12 -12L-12 12" strokeWidth="2.2" />
      </g>
      <g fill="#7fbeff" fontFamily="ui-monospace, monospace" letterSpacing="2">
        <text x="1680" y="408" fontSize="18">OBJ BRAVO · COMPOUND</text>
        <text x="1680" y="431" fontSize="12" opacity=".65">GRID 042 318 · HILL 182</text>
      </g>

      {/* Phase line */}
      <path d="M1370 1040L1888 490" fill="none" stroke="rgba(86,168,255,.22)" strokeWidth="1.4" strokeDasharray="5 10" />
      <text x="1505" y="897" fill="rgba(127,190,255,.40)" fontFamily="ui-monospace, monospace" fontSize="13" letterSpacing="2" transform="rotate(-46 1505 897)">PHASE LINE ECHO</text>
    </svg>
  );
};

export default TopographicMapFull;
